import { FC } from 'react';
import { AUTH_MODE_FILTERS_OPTIONS, DIRECTORY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import {
    formatRuleAction,
    formatUserResponse,
    getEnumLabelByValue,
    riskLevelToText
} from '@silverfort-nodelibs/common/utils/common';
import { LogEntry } from '../../types';

interface ExpandedLogContentProps {
    log: LogEntry;
    wallName?: string;
    isNtlmFailOpen?: boolean;
    riskIndicators: string[];
}

const calcAction = (log: LogEntry): string => {
    const [userResponse] = formatUserResponse(log.userResponse, log.effectiveRuleAction);
    let ruleAction = formatRuleAction(log.effectiveRuleAction, log.effectiveRuleId, true, log.protocolType) as string;

    const displayUserResponse = userResponse && (userResponse !== 'n/a' || ruleAction === 'MFA');
    if (ruleAction && displayUserResponse) {
        ruleAction += ` (${userResponse})`;
    }

    if (!ruleAction) {
        ruleAction = 'None';
    }

    return ruleAction;
};

export const ExpandedLogContent: FC<ExpandedLogContentProps> = ({ log, wallName, isNtlmFailOpen, riskIndicators }) => {
    return (
        <div className="flex flex-col gap-4 w-full h-fit bg-sf-gray-50 py-4 px-2">
            <div className="font-medium text-13 text-sf-gray-500">Auth</div>
            <div>
                <div className="text-13 text-sf-gray-500">Risk</div>
                <div className="font-medium text-13 text-sf-gray-800">{riskLevelToText(log.riskLevel)}</div>
            </div>
            <div>
                <div className="text-13 text-sf-gray-500">Auth Type</div>
                <div className="font-medium text-13 text-sf-gray-800">
                    {`${DIRECTORY_TYPES[log.directoryType || DIRECTORY_TYPES.ad.key].displayName} (${
                        log.protocolType
                    })`}
                </div>
            </div>
            <div>
                <div className="text-13 text-sf-gray-500">Silverfort Action</div>
                <div className="font-medium text-13 text-sf-gray-800">{calcAction(log)}</div>
            </div>
            <div className="font-medium text-13 text-sf-gray-500">Route</div>
            {log.domainController && (
                <div>
                    <div className="text-13 text-sf-gray-500">Domain Controller</div>
                    <div className="font-medium text-13 text-sf-gray-800">
                        {log.domainController?.toUpperCase() || 'None'}
                    </div>
                </div>
            )}
            <div>
                <div className="text-13 text-sf-gray-500">Silverfort Node</div>
                <div className="font-medium text-13 text-sf-gray-800">{wallName || 'None'}</div>
            </div>
            {log.tenantId && (
                <div>
                    <div className="text-13 text-sf-gray-500">Tenant ID</div>
                    <div className="font-medium text-13 text-sf-gray-800">{log.tenantId}</div>
                </div>
            )}

            <div>
                <div className="text-13 text-sf-gray-500">Request ID</div>
                <div className="font-medium text-13 text-sf-gray-800">{log.requestId}</div>
            </div>
            {(isNtlmFailOpen || log.authMode) && (
                <div>
                    <div className="text-13 text-sf-gray-500">Mode</div>
                    <div className="font-medium text-13 text-sf-gray-800">
                        {getEnumLabelByValue(AUTH_MODE_FILTERS_OPTIONS, log.authMode)}
                    </div>
                </div>
            )}
            {riskIndicators.length > 0 && (
                <>
                    <div className="font-medium text-13 text-sf-gray-500">Risk</div>
                    <div>
                        <div className="text-13 text-sf-gray-500">Risk Indicators</div>
                        <div className="w-full flex flex-wrap gap-2 font-medium text-13 text-sf-gray-800">
                            {riskIndicators.map((risk, i) => (
                                <span key={i}>
                                    {risk}
                                    {i < riskIndicators.length - 1 ? ',' : ''}
                                </span>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
